import { ColumnDef } from "@tanstack/react-table";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import { useTranslation } from "react-i18next";

import { AlertsTableConfig } from "../../../../@types/Alerts";
import { Notification } from "../../../../@types/Notification";
import {
  ISectionProps,
  PatientAlertsTableContent,
} from "../../../../@types/Patient";
import { IPortalPreferences } from "../../../../@types/Preferences";
import { ChevronDown } from "../../../../assets/icons/ChevronDown";
import { ChevronUp } from "../../../../assets/icons/ChevronUp";
import { Dots } from "../../../../assets/icons/Dots";
import { IncreaseArrow } from "../../../../assets/icons/IncreaseArrow";
import { TableSettings } from "../../../../assets/icons/TableSettings";
import { AlertsStatus } from "../../../../components/AlertStatus";
import { CustomIconButton } from "../../../../components/CustomIconButton";
import { Dropdown, DropdownTextItem } from "../../../../components/Dropdown";
import { createColumn } from "../../../../components/Table";
import { ActionContainer } from "../../../../components/TableActionDropdownButton/TableActionDropdownButton.styles";
import { queryClient } from "../../../../config/query";
import { AUTH_PORTAL_PREFERENCES } from "../../../../constants/localStorageKeys";
import { useInfiniteAlerts } from "../../../../hooks/queries/patients";
import useIsMobile from "../../../../hooks/useIsMobile";
import { useModal } from "../../../../hooks/useModal";
import { useToast } from "../../../../hooks/useToast";
import { useUserPreferences } from "../../../../hooks/useUserPreferences";
import { PageTitle } from "../../../../layout/components/PageTitle";
import { alertsService } from "../../../../services/alertsService";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { mixpanelActions } from "../../../../utils/mixpanel";
import { safeStorage, safeJsonParse } from "../../../../utils/storage";
import { formatTriggeredAlertSummary } from "../../../../utils/triggeredAlertLabels";
import { formatTriggerMeasurementType } from "../../../../utils/triggerLabels";
import { PatientAlertsTable } from "../PatientAlertsTable";
import { CreateTriggerModal } from "../PatientAlertsTable/components/CreateTriggerModal";
import { ResolveTriggerModal } from "../ResolveTriggerModal";
import {
  NewDataCOntainer,
  TableSettingsContainer,
  TitleContainer,
} from "./AlertsSection.styles";
import { AlertsTableConfigModal } from "./Components/AlertsTableConfigModal";
import { desktopTableColumns } from "./patientAlertsTableColumns";

export function AlertsSection({
  patient,
  updateColapsedSections,
  collapsedSection,
}: ISectionProps) {
  const { t } = useTranslation();

  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [alertsToResolve, setAlertsToResolve] = useState(false);

  const [showNewData, setShowNewData] = useState<boolean>(false);

  const { isMobile } = useIsMobile();
  const { openModal, closeModal } = useModal();
  const { showToast } = useToast();

  const {
    alerts: patientAlertTable,
    isError,
    isFetching,
    refetch,
    isRefetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteAlerts(patient.id);
  const { changePortalPreferences } = useUserPreferences();

  const [tableColumns, setTableColumns] =
    useState<ColumnDef<any, string>[]>(desktopTableColumns);

  const [alertsTableConfig, setAlertsTableConfig] = useState<AlertsTableConfig>(
    {
      type: true,
      trigger: true,
      value: true,
      summary: true,
      timeFrame: true,
      alertStatus: true,
      timeOfAlert: true,
      resolvedBy: true,
    }
  );

  const updateDropdownOptions = async () => {
    let updateDropdown = alertsToResolve;

    patientAlertTable?.forEach((patientAlert: PatientAlertsTableContent) => {
      if (patientAlert.alertStatus == null) {
        updateDropdown = true;
      } else {
        updateDropdown = updateDropdown || false;
      }
    });

    setAlertsToResolve(updateDropdown);
  };

  const handleUpdateDropdown = () => {
    setAlertsToResolve(false);
  };

  const handleRefreshTable = () => {
    refetch();
  };

  const handleTimeFrame = (timeframe: string | null) => {
    if (timeframe === null) {
      return t("patientPage_alerts_anyReading");
    }

    return t("patientPage_alerts_timeframeDays", { count: Number(timeframe) });
  };

  const handleColumns = (tableConfig: AlertsTableConfig) => {
    const columnsToAdd: ColumnDef<any, string>[] = [];
    const temporaryTableConfig = { ...tableConfig };

    if (temporaryTableConfig.type) {
      columnsToAdd.push(
        createColumn({
          keyName: "type",
          header: () => t("patientPage_alerts_columns_type"),
          cell: (info) => info.renderValue(),
        })
      );
    }

    if (temporaryTableConfig.trigger) {
      columnsToAdd.push(
        createColumn({
          keyName: "trigger",
          header: () => t("patientPage_alerts_columns_trigger"),
          cell: (info) => info.renderValue(),
        })
      );
    }

    if (temporaryTableConfig.value) {
      columnsToAdd.push(
        createColumn({
          keyName: "value",
          header: () => t("patientPage_alerts_columns_value"),
          cell: (info) => info.renderValue(),
        })
      );
    }

    if (temporaryTableConfig.summary) {
      columnsToAdd.push(
        createColumn({
          keyName: "summary",
          header: () => t("patientPage_alerts_columns_summary"),
          cell: (info) => info.renderValue(),
          size: 300,
        })
      );
    }

    if (temporaryTableConfig.timeFrame) {
      columnsToAdd.push(
        createColumn({
          keyName: "timeFrame",
          header: () => t("patientPage_alerts_columns_timeFrame"),
          cell: (info) => info.renderValue(),
        })
      );
    }

    if (temporaryTableConfig.alertStatus) {
      columnsToAdd.push(
        createColumn({
          keyName: "alertStatus",
          header: () => t("patientPage_alerts_columns_status"),
          cell: (info) => info.renderValue(),
        })
      );
    }

    if (temporaryTableConfig.timeOfAlert) {
      columnsToAdd.push(
        createColumn({
          keyName: "timeOfAlert",
          header: () => t("patientPage_alerts_columns_timeOfAlert"),
          cell: (info) => info.renderValue(),
          size: 200,
        })
      );
    }

    if (temporaryTableConfig.resolvedBy) {
      columnsToAdd.push(
        createColumn({
          keyName: "resolvedBy",
          header: () => t("patientPage_alerts_columns_resolvedBy"),
          cell: (info) => info.renderValue(),
        })
      );
    }

    columnsToAdd.push(
      createColumn({
        keyName: "action",
        header: () => "",
        cell: (info) => info.renderValue(),
        size: 30,
      })
    );

    setTableColumns(columnsToAdd);
    setAlertsTableConfig(temporaryTableConfig);

    const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);

    if (portalPreferences) {
      const parsedPortalPreferences = safeJsonParse<
        IPortalPreferences | undefined
      >(portalPreferences, undefined);

      if (parsedPortalPreferences) {
        const configJson = JSON.stringify(temporaryTableConfig);
        parsedPortalPreferences.alertsTableColumnPreferences = configJson;

        const portalPreferencesJson = JSON.stringify(parsedPortalPreferences);
        safeStorage.setItem(AUTH_PORTAL_PREFERENCES, portalPreferencesJson);
        changePortalPreferences(parsedPortalPreferences);
      }
    }
  };

  useEffect(() => {
    mixpanelActions.track("Visited Screen: Billing table");
    const portalPreferences = safeStorage.getItem(AUTH_PORTAL_PREFERENCES);
    const parsedPortalPreferences = safeJsonParse<
      IPortalPreferences | undefined
    >(portalPreferences, undefined);

    if (
      parsedPortalPreferences?.alertsTableColumnPreferences &&
      parsedPortalPreferences?.alertsTableColumnPreferences !== ""
    ) {
      const parsedAlertsTableConfig = JSON.parse(
        parsedPortalPreferences?.alertsTableColumnPreferences
      ) as AlertsTableConfig;

      setAlertsTableConfig(parsedAlertsTableConfig);
      handleColumns(parsedAlertsTableConfig);
    } else {
      handleColumns(alertsTableConfig);
    }
  }, []);

  useEffect(() => {
    updateDropdownOptions().then();

    if (
      collapsedSection.value === true &&
      !isFetching &&
      !isRefetching &&
      patientAlertTable.length > 0
    ) {
      const mostRecentAlertDate = dayjs(patientAlertTable[0].timeOfAlert);
      const collapsedDate = dayjs(collapsedSection.collapsedDate);

      if (mostRecentAlertDate > collapsedDate) {
        setShowNewData(true);
      }
    }
  }, [patientAlertTable]);

  const parsePatientAlertsToTable = useMemo(() => {
    if (!patientAlertTable) return [];

    const getOptions = (alertType: string) => {
      // EN-5191: ids, not labels. The handler below matched on the English menu text, so
      // translating it would have stopped resolving and editing alerts working.
      const baseOptions = ["markAllResolved", "markResolved"];
      // Only show "Edit trigger" for non-Workflow alerts
      if (alertType !== "Workflow") {
        baseOptions.push("editTrigger");
      }
      return baseOptions;
    };
    const handleOnChangeDropdownVisibility = () => {
      setIsDropDownOpen(!isDropDownOpen);
    };

    const handleNotification = (message: string) => {
      showToast(message, "success");
    };

    const handleCloseModal = (
      isMultiple: boolean,
      resolved: boolean,
      type: string
    ) => {
      if (resolved) {
        if (isMultiple) {
          mixpanelActions.track("User Action: Resolve All Alerts");
          handleNotification(t("patientPage_alerts_allResolved"));
        } else {
          mixpanelActions.track("User Action: Resolve Alert");
          handleNotification(t("patientPage_alerts_resolvedToast", { type }));
        }
        setAlertsToResolve(false);
        refetch();
      }
      closeModal();
    };

    const markAllResolved = () => {
      const idList: string[] = [];
      patientAlertTable.forEach((element: PatientAlertsTableContent) => {
        if (element.alertStatus === null) {
          idList.push(element.id);
        }
      });
      openModal(
        <ResolveTriggerModal
          patientId={patient.id}
          alertIds={idList}
          type={t("patientPage_alerts_multipleSelected")}
          triggered={t("patientPage_alerts_multipleSelected")}
          isMultiple
          onClose={(resolved) => handleCloseModal(true, resolved, "")}
        />,
        {
          width: "auto",
          height: "auto",
          showCloseButton: true,
        }
      );
    };

    // `typeLabel` is the row's translated type, shown in the modal and the toast (EN-5284).
    const markAsResolved = (
      patientAlert: PatientAlertsTableContent,
      typeLabel: string
    ) => {
      if (patientAlert.alertStatus !== "Resolved") {
        openModal(
          <ResolveTriggerModal
            patientId={patient.id}
            alertIds={[patientAlert.id]}
            type={typeLabel}
            triggered={patientAlert.timeOfAlert.toString()}
            onClose={(showNotification, success) =>
              showNotification
                ? handleCloseModal(false, success ?? false, typeLabel)
                : closeModal()
            }
          />,
          {
            width: "auto",
            height: "auto",
            showCloseButton: true,
          }
        );
      }
    };

    const editTrigger = (alertId: string) => {
      const handleNotification = (
        notificationMessage: string,
        notificationType: "success" | "warning" | "error"
      ) => {
        showToast(notificationMessage, notificationType);
      };

      alertsService.getTriggerBytAlertId(alertId).then((trigger) => {
        const { data } = trigger;

        openModal(
          <CreateTriggerModal
            content={data}
            isEdit
            measurementAlertId={data.measurementAlertId}
            patientId={patient.id}
            setParentNotification={handleNotification}
            onClose={() => {
              closeModal();
              queryClient.refetchQueries({
                queryKey: [`triggers-table-${patient.id}`],
              });
            }}
          />,
          {
            width: "auto",
            height: "auto",
            showCloseButton: true,
          }
        );
      });
    };

    return patientAlertTable.map((patientAlert: PatientAlertsTableContent) => {
      const isActive = patientAlert.alertStatus == null;

      const parsedContent = ReactHtmlParser(patientAlert.alertNote ?? "");

      const htmlTagRegex = /(<([^>]+)>)/gi;
      const noteWithouTags = patientAlert.alertNote
        ?.replace(htmlTagRegex, "")
        .slice(0, 20);

      const handleTriggerText = (triggerText: string) => {
        if (triggerText.includes("+")) {
          const triggerWithoutPlus = triggerText.replace("+", "");

          return (
            <div
              style={{
                display: "flex",
                gap: "2px",
                marginLeft: -4,
              }}
            >
              <IncreaseArrow /> {triggerWithoutPlus}
            </div>
          );
        }

        return triggerText;
      };

      const handleValue = () => {
        if (!patientAlert.value) return "-";

        return `${patientAlert.value}${
          patientAlert.spirometryAcceptance
            ? ` (${patientAlert.spirometryAcceptance})`
            : ""
        }`;
      };

      // EN-5284: the type and summary in the user's language. Display only — the checks below
      // ("Workflow", the row menu) keep reading the API's own patientAlert.type.
      const typeLabel = formatTriggerMeasurementType(t, {
        measurementType: patientAlert.type,
        measurementField: patientAlert.measurementField,
        measurementField2: patientAlert.measurementField2,
      });
      const summaryText =
        typeof patientAlert.summary === "string"
          ? formatTriggeredAlertSummary(
              t,
              patientAlert.summary,
              patientAlert.summaryParts
            )
          : patientAlert.summary;

      const handleSummary = () => {
        if (typeof summaryText === "string") {
          if (summaryText.length <= 30) {
            return summaryText;
          }

          return (
            <Tooltip
              title={summaryText}
              zIndex={3002}
              color="#4B4B4B"
              arrow={false}
              styles={{
                body: {
                  minHeight: "48px",
                  minWidth: "146px",
                  borderRadius: "4px !important",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                },
              }}
            >
              {summaryText.substring(0, 30)}...
            </Tooltip>
          );
        }

        return "";
      };

      return {
        id: patientAlert.id,
        type: typeLabel,
        trigger: handleTriggerText(patientAlert.trigger),
        timeFrame:
          patientAlert.type !== "Workflow"
            ? handleTimeFrame(patientAlert.timeFrame)
            : "",
        spirometryAcceptance: patientAlert.spirometryAcceptance || "",
        value: handleValue(),
        timeOfAlert: getLongFormattedDate(patientAlert.timeOfAlert),
        summary: handleSummary(),
        alertStatus: isActive ? (
          <AlertsStatus>{t("patientPage_alerts_active")}</AlertsStatus>
        ) : (
          t("patientPage_alerts_resolved")
        ),
        isActive,
        resolvedBy: patientAlert.resolvedBy || "",
        hcpNotification: patientAlert.hcpNotification,
        created: patientAlert.created,
        alertNote:
          patientAlert.alertNote !== null &&
          patientAlert.alertNote !== undefined ? (
            <div>
              <Tooltip
                title={<div>{parsedContent}</div>}
                arrow={false}
                placement="bottom"
                styles={{
                  body: {
                    padding: "6px 12px",
                    minHeight: "40px",
                    width: "fit-content",
                    maxWidth: "220px",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: "'Open Sans', sans-serif",
                  },
                }}
              >
                {`${noteWithouTags}${
                  noteWithouTags && noteWithouTags.length === 20 ? "..." : ""
                }`}
              </Tooltip>
            </div>
          ) : (
            "-"
          ),
        action: (
          <div>
            {alertsToResolve && isActive ? (
              <ActionContainer
                className="actionContainer"
                onClick={(e) => e.stopPropagation()}
              >
                <Dropdown
                  value=""
                  trigger={
                    <CustomIconButton>
                      <Dots />
                    </CustomIconButton>
                  }
                  onOpenChange={handleOnChangeDropdownVisibility}
                  onValueChange={(val: string) => {
                    if (val === "markAllResolved") {
                      markAllResolved();
                    }
                    if (val === "markResolved") {
                      markAsResolved(patientAlert, typeLabel);
                    }
                    if (val === "editTrigger") {
                      editTrigger(patientAlert.id);
                    }
                  }}
                >
                  {getOptions(patientAlert.type).map((option) => (
                    <DropdownTextItem
                      key={option}
                      value={option}
                      text={t(`patientPage_alerts_actions_${option}`)}
                    />
                  ))}
                </Dropdown>
              </ActionContainer>
            ) : (
              ""
            )}
          </div>
        ),
      } as PatientAlertsTableContent;
    });
  }, [patientAlertTable, t]);

  const handleCollapseSection = () => {
    if (collapsedSection.value === true) {
      setShowNewData(false);
    }
    updateColapsedSections("alertsSection");
  };

  const handleOnAlertsTableConfigModalClose = (
    tableConfig: AlertsTableConfig,
    notification?: Notification
  ) => {
    closeModal();
    handleColumns(tableConfig);

    if (notification !== undefined) {
      showToast(notification.message, notification.type);
    }
  };

  const handleTableConfigClick = () => {
    mixpanelActions.track("User Action: ConfigureAlertTable");

    openModal(
      <AlertsTableConfigModal
        columnsConfig={alertsTableConfig}
        onClose={handleOnAlertsTableConfigModalClose}
      />,
      {
        width: "655px",
        maxHeight: "628px",
        showCloseButton: true,
      }
    );
  };

  return (
    <>
      {!isMobile && (
        <TitleContainer>
          <PageTitle>{t("patientPage_sections_alerts")}</PageTitle>
          <Tooltip
            title={t("patientPage_alerts_configureColumns")}
            arrow={false}
            placement="right"
            styles={{
              body: {
                padding: "6px 12px",
                minHeight: "40px",
                width: "max-content",
                minWidth: "220px",
                borderRadius: "4px !important",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "'Open Sans', sans-serif",
              },
            }}
          >
            <TableSettingsContainer
              role="button"
              tabIndex={0}
              onClick={handleTableConfigClick}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleTableConfigClick();
                }
              }}
            >
              <TableSettings />
            </TableSettingsContainer>
          </Tooltip>
          {showNewData && (
            <NewDataCOntainer>
              {t("patientPage_sections_newData")}
            </NewDataCOntainer>
          )}
          <CustomIconButton
            style={{ marginLeft: "2px" }}
            onClick={handleCollapseSection}
          >
            {collapsedSection.value ? <ChevronUp /> : <ChevronDown />}
          </CustomIconButton>
        </TitleContainer>
      )}
      <div
        style={{
          height: collapsedSection.value ? "0" : "",
          overflow: collapsedSection.value ? "hidden" : "",
        }}
      >
        <PatientAlertsTable
          columns={tableColumns}
          patientId={patient.id}
          isLoading={isFetching && !isRefetching && !isFetchingNextPage}
          isError={isError}
          content={parsePatientAlertsToTable}
          updateDropdown={handleUpdateDropdown}
          refreshTable={handleRefreshTable}
          showManageTriggersButton={!collapsedSection.value}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          useInfiniteScroll
        />
      </div>
    </>
  );
}
